/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Logs} from "../APIClient.jsx";
import Console from "./Console.jsx";
import DisplayIcon from "./Icons/DisplayIcon.jsx";
import {formatDuration, intervalToDuration} from "date-fns";
import Tooltip from "./Tooltip.jsx";
import {CgSpinner} from "react-icons/cg";
import useInterval from "../hooks/useInterval.jsx";

function BuildLine(props) {
    const [isOpen, setIsOpen] = useState(false)

    let status = props.step.status
    let hasError = status === "error"


    useEffect(() => {
        if (hasError && isOpen === false) {
            setIsOpen(true)
        }
    }, [])

    const duration = formatDuration(intervalToDuration({
        start: 0, end: (props.step.time ?? 0) * 1000
    }));


    return <div className={"group  rounded overflow-hidden"}>
        <div
            className={` flex h-8 items-center p-2 rounded cursor-pointer hover:bg-stone-50 sticky top-0 ${hasError && "text-red-700"}`}
            onClick={() => setIsOpen(!isOpen)}>
            <div className={"mr-2"}>
                <DisplayIcon iconName={"PiCaretRight"}
                             className={`transition-transform duration-200 size-3! ${isOpen && "rotate-90"}`}/>
            </div>
            {status === "done" && (<div className={"flex items-center gap-2 p-1 shrink-0"}>

                <div className={"size-4 flex items-center justify-center bg-gray-500 rounded-full "}>
                    <DisplayIcon iconName={"PiCheck"} className={"text-white size-2.5!"}/>

                </div>
            </div>)}
            {status === "cached" && (<div className={"flex items-center gap-2 p-1"}>

                <Tooltip text={<div className={"text-blue-500"}>
                    The step was Cached
                </div>}>
                    <DisplayIcon iconName={"IoMdCheckmarkCircleOutline"} className={"text-blue-700"}/>

                </Tooltip>
            </div>)}

            {status === "in_progress" && (<div className={"flex items-center gap-2 p-1 shrink-0"}>
                <div className={"text-blue-500"}>
                    In Progress...
                </div>

                <CgSpinner
                    className={`w-4 h-4 animate-spin  will-change-transform `}/>
            </div>)}
            {status === "error" && (<div className={"flex items-center gap-2 p-1"}>
                {/*<div className={"text-red-500 text-sm font-light"}>*/}
                {/*    has error*/}
                {/*</div>*/}
                <DisplayIcon iconName={"RiAlarmWarningLine"} className={"text-red-700"}/>
            </div>)}
            {/*<div className={"text-xl font-serif w-8 p-1 text-right shrink-0"}>*/}
            {/*    {props.step.step.toString().padStart(2, '0')}*/}
            {/*</div>*/}
            <div className={"grow ml-2 text-sm truncate text-stone-500"}>
                {props.step.description}
            </div>


            <div className={"text-sm font-light shrink-0"}>
                {duration}
            </div>


        </div>

        {isOpen && (<div className={"p-2 bg-stone-100 pl-12"}>
            <Console logs={props.step.outputs}/>

            {props.step.outputs.map(logLine => {

                return (<div>{logLine.message}</div>)
            })}
        </div>)}
    </div>;
}

BuildLine.propTypes = {step: PropTypes.any};

const STEP_PREFIX_REGEX = /^#(\d+)\s+(.*)/;
const TYPE_MATCH_REGEX = /^\[([^\]]+)\]\s+(.*)/; // Matches [internal] ..., [auth] ...
const DONE_REGEX = /^DONE\s+([\d\.]+)s/;
const CACHED_REGEX = /^CACHED/;
const ERROR_REGEX = /^ERROR/;
const TRANSFER_REGEX = /^transferring\s+(.*)\s+([\d\.]+)s/;
const RESULT_REGEX = /^\[RESULT\]\s+/;

const parseDockerLogs = (messages) => {
    const stepMap = {};

    messages.forEach(({log}) => {
        const prefixMatch = log.match(STEP_PREFIX_REGEX);

        if (prefixMatch) {
            const stepNumber = parseInt(prefixMatch[1], 10);
            const payload = prefixMatch[2].trim(); // The rest of the log line

            // Initialize the step in the map if not already there
            if (!stepMap[stepNumber]) {
                stepMap[stepNumber] = {
                    step: stepNumber,
                    description: `Step ${stepNumber}`, // Fallback description
                    type: 'run',
                    status: 'in_progress',
                    outputs: [],
                    time: null
                };
            }

            const step = stepMap[stepNumber];

            // Evaluate payload content
            const typeMatch = payload.match(TYPE_MATCH_REGEX);
            const doneMatch = payload.match(DONE_REGEX);
            const cachedMatch = payload.match(CACHED_REGEX);
            const errorMatch = payload.match(ERROR_REGEX);
            const transferMatch = payload.match(TRANSFER_REGEX);

            if (typeMatch) {
                step.type = typeMatch[1]; // e.g., 'internal', 'auth'
                step.description = typeMatch[2];
            } else if (doneMatch) {
                step.status = 'done';
                step.time = doneMatch[1];
            } else if (cachedMatch) {
                step.status = 'cached';
            } else if (errorMatch) {
                step.status = 'error';
            } else if (transferMatch) {
                step.description = `transferring ${transferMatch[1]}`;
                step.time = transferMatch[2];
            }

            // Always store the log payload
            step.outputs.push({log: payload});

        } else if (RESULT_REGEX.test(log)) {
            const stepNumber = Object.keys(stepMap).length + 1;
            if (!stepMap[stepNumber]) {
                stepMap[stepNumber] = {
                    step: stepNumber,
                    description: "Build done",
                    status: 'done',
                    outputs: [{log: log.trim()}],
                    time: null
                };
            }
        }
    });

    return Object.values(stepMap).sort((a, b) => b.step - a.step);
};


const BuildLogs = ({buildId}) => {
    const [logs, setLogs] = useState([]);
    const [parsedLogs, setParsedLogs] = useState([]);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        let fetchedLogs = await Logs.getBuildLogs(buildId);
        if (fetchedLogs.error) {
            setError("Failed to fetch logs. Displaying cached logs.");
            return;
        }

        let messages = (fetchedLogs?.logs ?? []);
        setError(null);

        // Use the new single-pass, external parsing function
        const newlyParsedLogs = parseDockerLogs(messages);

        setLogs(messages);
        setParsedLogs(newlyParsedLogs);
    }, [buildId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useInterval(() => {
        fetchData();
    }, 5000);

    return (<div
        className={"space-y-1 h-[40rem] w-full overflow-y-scroll customScrollbarSmall p-2 border bg-white rounded relative"}>


        {/*{parsedLogs.map(step => {*/}
        {/*    return (<BuildLine key={step.step} step={step}/>)*/}
        {/*})}*/}
        <Console logs={logs} titel={"Build logs"} error={error}/>
    </div>);
};


BuildLogs.propTypes = {};

BuildLogs.defaultProps = {};


export default BuildLogs;